import React, { useState } from 'react'
import './datatable.css'
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  getFilteredRowModel,
} from "@tanstack/react-table";
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import Skeleton from '@mui/material/Skeleton';

const styleSx = {
  odd: {
    background: '#F6F6F6 !important'
  },
  even: {

    background: '#ffffff !important'
  }
}
const Datatable = ({
    data = [],
    columns = [],
    isLoading = false,
    totalRecords = 0,
    currentPage = 1,
    pageSize = 10,
    onPageChange,
    onPageSizeChange,
    onFilterChange,
    onSortingChange,
    enableFiltering = true,
    enablePagination = true,
    enableSorting = true,
    initialColumnFilters = [],
    className = '',
    noDataText = "No results found !",
    pageSizeOptions = [10, 20, 50, 100],
    isServerSide = false,
    initialPageSize = 10
}) => {

    const [sorting, setSorting] = useState([]);
    const [columnFilters, setColumnFilters] = useState(initialColumnFilters);
    const [pagination, setPagination] = useState({
        pageIndex: isServerSide ? currentPage - 1 : 0,
        pageSize: isServerSide ? pageSize : initialPageSize,
    });

    // Handle pagination change
    const handlePaginationChange = (updater) => {
        const newPagination = typeof updater === 'function' ? updater(pagination) : updater;
        setPagination(newPagination);

        if (onPageChange && isServerSide) {
            onPageChange(newPagination.pageIndex + 1, newPagination.pageSize);
        }
    };

    // Handle page size change
    const handlePageSizeChange = (newPageSize) => {
        const newPagination = {
            pageIndex: 0,
            pageSize: newPageSize,
        };
        setPagination(newPagination);

        if (onPageSizeChange && isServerSide) {
            onPageSizeChange(newPageSize);
        }
        if (onPageChange && isServerSide) {
            onPageChange(1, newPageSize);
        }
    };

    // Handle filter change
    const handleColumnFiltersChange = (updater) => {
        const newFilters = typeof updater === 'function' ? updater(columnFilters) : updater;
        setColumnFilters(newFilters);

        if (onFilterChange && isServerSide) {
            onFilterChange(newFilters);
        }
    };

    const handleSortingChange = (updater) => {
        const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
        setSorting(newSorting);

        if (onSortingChange && isServerSide) {
            onSortingChange(newSorting);
        }
    };

    // Create table instance
    const table = useReactTable({
        data,
        columns,
        state: {
            sorting,
            columnFilters,
            pagination,
        },
        onSortingChange: handleSortingChange,
        onColumnFiltersChange: handleColumnFiltersChange,
        onPaginationChange: handlePaginationChange,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        manualPagination: isServerSide,
        manualSorting: isServerSide,
        manualFiltering: isServerSide,
        pageCount: isServerSide ? Math.ceil(totalRecords / (isServerSide ? pageSize : pagination.pageSize)) : undefined,
        rowCount: isServerSide ? totalRecords : undefined,
    });

    const renderColumnFilter = (column) => {
        if (column.columnDef.Filter) {
            return flexRender(column.columnDef.Filter, { column });
        }
        // fallback for text input:
        const columnFilterValue = column.getFilterValue();
        const hasFilter = columnFilterValue && columnFilterValue.length > 0;

        return (
            <OutlinedInput
                type="text"
                value={columnFilterValue ?? ''}
                onChange={(e) => column.setFilterValue(e.target.value)}
                placeholder={`Search ${column.columnDef.header.toLowerCase()}`}
                className="column-filter-inputBox"
                size="small"
                endAdornment={
                    <InputAdornment position="end">
                        {!hasFilter && <FilterAltIcon className='filtericon' />}
                        {hasFilter && (
                            <IconButton
                                onClick={() => column.setFilterValue('')}
                                size="small"
                                className='iconButton'
                                title="Clear filter"
                            >
                                <FilterAltOffIcon className='filtericon' />
                            </IconButton>
                        )}
                    </InputAdornment>
                }
            />
        );
    };

    const getSkeleton = () => {
        let arr = [];
        for (let i = 0; i < 10; i++) {
            arr.push(<Skeleton key={i} sx={i % 2 === 0 ? styleSx.odd : styleSx.even} variant="rect" width="100%" height="50px" />);
        }
        return arr;
    };

    // Calculate pagination info for server-side and client-side
    const getDisplayInfo = () => {
        if (isServerSide) {
            const startItem = (currentPage - 1) * pageSize + 1;
            const endItem = Math.min(currentPage * pageSize, totalRecords);
            return { startItem, endItem, totalItems: totalRecords };
        } else {
            const startItem = table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1;
            const endItem = Math.min((table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize, table.getFilteredRowModel().rows.length);
            const totalItems = table.getFilteredRowModel().rows.length;
            return { startItem, endItem, totalItems };
        }
    };

    const displayInfo = getDisplayInfo();
    return (
        <>
            <div className={` ${className}`}>
                <div className="tanstack-tableContainer">
                    <table className='tanstack-tableLayout'>
                        <thead>
                            {table.getHeaderGroups().map(headerGroup => (
                                <React.Fragment key={headerGroup.id}>
                                    <tr>
                                        {headerGroup.headers.map(header => (
                                            <th
                                                key={header.id}
                                                style={{
                                                    width: header.column.columnDef.width || `${header.getSize()}px`,
                                                    cursor: (enableSorting && header.column.getCanSort()) ? "pointer" : "default",
                                                }}
                                                onClick={enableSorting ? header.column.getToggleSortingHandler() : undefined}
                                            >
                                                <div className='th-cell'>
                                                    <span>
                                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                                    </span>
                                                    {enableSorting && header.column.getCanSort() && (
                                                        <span>
                                                            {{ asc: "↑", desc: "↓" }[header.column.getIsSorted()] ?? ""}
                                                        </span>
                                                    )}
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                    {/* Filter row */}
                                    {enableFiltering && (
                                        <tr>
                                            {headerGroup.headers.map(header => (
                                                <th key={`${header.id}-filter`} className='header-group' style={{
                                                    width: header.column.columnDef.width || `${header.getSize()}px`,
                                                }}>
                                                    {header.column.getCanFilter() && renderColumnFilter(header.column)}
                                                </th>
                                            ))}
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan={columns.length}>
                                        {getSkeleton()}
                                    </td>
                                </tr>
                            ) :
                                table.getRowModel().rows.length === 0 ? (
                                    <tr className='no-data-row'>
                                        <td colSpan={columns.length}>
                                            {noDataText}
                                        </td>
                                    </tr>
                                ) : (
                                    table.getRowModel().rows.map((row, index) => (
                                        <tr key={row.id} className={index % 2 === 0 ? 'data-row-even' : 'data-row-odd'}>
                                            {row.getVisibleCells().map((cell, cellIndex) => {
                                                let cellClassName = cell.column.columnDef.meta?.className || '';
                                                let cellStyle = cell.column.columnDef.meta?.style || {};
                                                let cellOnClick = cell.column.columnDef.meta?.onClick || null;

                                                if (typeof cell.column.columnDef.meta === 'function') {
                                                    const dynamicMeta = cell.column.columnDef.meta({ row, cell });
                                                    cellClassName = dynamicMeta?.className || '';
                                                    cellStyle = dynamicMeta?.style || {};
                                                    cellOnClick = dynamicMeta?.onClick || null;
                                                }

                                                return (
                                                    <td
                                                        key={cell.id}
                                                        className={`data-cell ${cellClassName}`}
                                                        style={cellStyle}
                                                        onClick={cellOnClick}
                                                        data-column={cell.column.id}
                                                    >
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))
                                )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {enablePagination && (
                    <div className='tanstack-pagination-container'>
                        <div className='left-section-pagination'>
                            {/* Navigation Controls */}
                            <div className='navigation-control-container'>
                                {/* First Page */}
                                <button
                                    onClick={() => isServerSide ? onPageChange(1, isServerSide ? pageSize : table.getState().pagination.pageSize) : table.setPageIndex(0)}
                                    disabled={isServerSide ? currentPage === 1 : !table.getCanPreviousPage()}
                                    className={`navigation-button ${(isServerSide ? currentPage === 1 : !table.getCanPreviousPage()) ? 'navigation-button-disabled' : ''}`}
                                    title="First page"
                                >
                                    <KeyboardDoubleArrowLeftIcon className='navigation-icon' />
                                </button>

                                {/* Previous Page */}
                                <button
                                    onClick={() => isServerSide ? onPageChange(currentPage - 1, isServerSide ? pageSize : table.getState().pagination.pageSize) : table.previousPage()}
                                    disabled={isServerSide ? currentPage === 1 : !table.getCanPreviousPage()}
                                    className={`navigation-button ${(isServerSide ? currentPage === 1 : !table.getCanPreviousPage()) ? 'navigation-button-disabled' : ''}`}
                                    title="Previous page"
                                >
                                    <KeyboardArrowLeftIcon className='navigation-icon' />
                                </button>

                                {/* Page Numbers */}
                                <div className='pagenumber-container'>
                                    {(() => {
                                        const current = isServerSide ? currentPage : table.getState().pagination.pageIndex + 1;
                                        const total = isServerSide ? Math.ceil(totalRecords / pageSize) : table.getPageCount();
                                        const pageButtons = [];

                                        if (total <= 5) {
                                            // Show all pages if 5 or fewer
                                            for (let i = 1; i <= total; i++) {
                                                pageButtons.push(i);
                                            }
                                        } else {
                                            // Smart pagination logic
                                            if (current <= 3) {
                                                pageButtons.push(1, 2, 3, 4, 5);
                                            } else if (current >= total - 2) {
                                                pageButtons.push(total - 4, total - 3, total - 2, total - 1, total);
                                            } else {
                                                pageButtons.push(current - 2, current - 1, current, current + 1, current + 2);
                                            }
                                        }

                                        return pageButtons.map((pageNum) => (
                                            <button
                                                key={pageNum}
                                                onClick={() => isServerSide ? onPageChange(pageNum, isServerSide ? pageSize : table.getState().pagination.pageSize) : table.setPageIndex(pageNum - 1)}
                                                disabled={current === pageNum}
                                                className={`pagenumber-button ${current === pageNum ? 'pagenumber-button-active' : ''}`}
                                            >
                                                {pageNum}
                                            </button>
                                        ));
                                    })()}
                                </div>

                                {/* Next Page */}
                                <button
                                    onClick={() => isServerSide ? onPageChange(currentPage + 1, isServerSide ? pageSize : table.getState().pagination.pageSize) : table.nextPage()}
                                    disabled={isServerSide ? currentPage >= Math.ceil(totalRecords / pageSize) : !table.getCanNextPage()}
                                    className={`navigation-button ${(isServerSide ? currentPage >= Math.ceil(totalRecords / pageSize) : !table.getCanNextPage()) ? 'navigation-button-disabled' : ''}`}
                                    title="Next page"
                                >
                                    <KeyboardArrowRightIcon className='navigation-icon' />
                                </button>

                                {/* Last Page */}
                                <button
                                    onClick={() => isServerSide ? onPageChange(Math.ceil(totalRecords / pageSize), isServerSide ? pageSize : table.getState().pagination.pageSize) : table.setPageIndex(table.getPageCount() - 1)}
                                    disabled={isServerSide ? currentPage >= Math.ceil(totalRecords / pageSize) : !table.getCanNextPage()}
                                    className={`navigation-button ${(isServerSide ? currentPage >= Math.ceil(totalRecords / pageSize) : !table.getCanNextPage()) ? 'navigation-button-disabled' : ''}`}
                                    title="Last page"
                                >
                                    <KeyboardDoubleArrowRightIcon className='navigation-icon' />
                                </button>
                            </div>

                            <div className='items-select-container'>
                                <Autocomplete
                                    value={isServerSide ? pageSize : table.getState().pagination.pageSize}
                                    onChange={(event, newValue) => {
                                        if (newValue) {
                                            if (isServerSide) {
                                                handlePageSizeChange(Number(newValue));
                                            } else {
                                                setPagination({
                                                    pageIndex: 0,
                                                    pageSize: Number(newValue),
                                                })
                                            }
                                        }
                                    }}
                                    options={pageSizeOptions}
                                    getOptionLabel={(option) => option.toString()}
                                    disableClearable
                                    size="small"
                                    className='items-select-dropdown'
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                        />
                                    )}
                                />
                                <span className='items-select-text'>items per page</span>
                            </div>
                        </div>

                        {/* Right section: Items count */}
                        <div className='items-count-container'>
                            <strong className='items-count-digit'>
                                {displayInfo.startItem}
                            </strong>
                            {" - "}
                            <strong className='items-count-digit'>
                                {displayInfo.endItem}
                            </strong>
                            {" of "}
                            <strong className='items-count-digit'>{displayInfo.totalItems}</strong>
                            {" items"}
                        </div>
                    </div>
                )}
            </div>
        </>
    )
}

export default Datatable
